import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 bg-white shadow-md border-b border-slate-100`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <div className="flex flex-col">
              <span className="font-heading font-bold text-pool-deep text-lg leading-tight tracking-tight">
                Pete's Pool
              </span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-pool-aqua font-semibold leading-tight">
                Service & Repair
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                  location.pathname === link.path
                    ? "text-pool-aqua"
                    : "text-pool-deep/70 hover:text-pool-deep"
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-pool-aqua/8 rounded-full"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Phone + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:5599996208"
              data-testid="nav-phone"
              className="flex items-center gap-2 text-pool-deep/80 hover:text-pool-deep transition-colors text-sm font-medium"
            >
              <Phone size={15} className="text-pool-aqua" />
              (559) 999-6208
            </a>
            <a
              href="tel:5599996208"
              data-testid="nav-call-now"
              className="bg-pool-deep text-white hover:bg-pool-aqua px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-pool-aqua/25 hover:-translate-y-0.5"
            >
              Call Now
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg text-pool-deep hover:bg-pool-aqua/10 transition-colors"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "bg-pool-aqua/10 text-pool-aqua"
                      : "text-pool-deep/70 hover:bg-slate-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-slate-100 mt-4">
                <a
                  href="tel:5599996208"
                  data-testid="mobile-call-btn"
                  className="flex items-center justify-center gap-2 bg-pool-deep text-white py-3.5 rounded-full font-semibold text-base"
                >
                  <Phone size={18} />
                  (559) 999-6208
                </a>
                <p className="text-center text-xs text-pool-deep/50 mt-2 font-accent italic">
                  Se Habla Español
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
